// OP-3605 — Entrada (ESL16): como o patrimônio nasce no sistema. Duas formas,
// porque são dois regimes: peça com série entra uma a uma, com serial e
// patrimônio próprios; consumível entra por quantidade.
//
// Em ambos os casos a entrada é um LANÇAMENTO no razão, não um UPDATE de
// saldo: é por isso que ela pede nota fiscal e fica no histórico da peça.
import { FC, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueItemDetalhe, useEstoqueItensSolicitacao, useEstoqueLocais } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { createEstoqueEntradaQuantidade, createEstoqueUnidade, getEstoqueUnidadePorCodigo } from '../../../../Service/estoque'
import { ORIGEM_ENTRADA_LABEL, ORIGENS_ENTRADA, OrigemEntrada, UnidadePorCodigo } from '../../../../types/estoque'
import {
  BD, Button, Card, Field, Input, PageSubtitle, PageTitle, SEVERIDADE, Select, SoDesktop, Spinner,
} from '../../styles'
import { BarraFixa } from '../BarraFixa'
import { CampoComLeitor } from '../CampoComLeitor'
import { tratarErro } from '../erros'
import { Ancora } from '../RefLink'
import { hojeISO } from '../Solicitar/form'
import {
  CampoDuplicado, campoDuplicado, explicacaoOrigem, impedimentoAtributos, impedimentoCadastroPeca, impedimentoEntradaQuantidade,
  impedimentoOrigemEntrada, pecaJaCadastrada,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'
import { MoneyInputOpcional } from '../../MoneyInput'

export const EstoqueEntrada: FC = () => {
  const navigate = useNavigate()
  const hoje = useMemo(hojeISO, [])
  const { pode } = usePerfilEstoque()
  const podePatrimonio = pode('PATRIMONIO')
  const podeVerPeca = pode('VISUALIZAR')
  const { locais, isLoading: carregandoLocais, isError: erroLocais, isUnauthorized, mutate: recarregarLocais } = useEstoqueLocais()
  const itensQ = useEstoqueItensSolicitacao()
  const [itemId, setItemId] = useState<number | null>(null)
  const [localId, setLocalId] = useState<number | null>(null)
  const detalheQ = useEstoqueItemDetalhe(itemId)
  const item = itensQ.data.find((i) => i.id === itemId)
  const ehSerial = item?.controle === 'SERIAL'

  const [serial, setSerial] = useState('')
  const [patrimonio, setPatrimonio] = useState('')
  const [serialFabricante, setSerialFabricante] = useState('')
  const [fornecedor, setFornecedor] = useState('')
  const [origem, setOrigem] = useState<OrigemEntrada>('COMPRA')
  const [notaFiscal, setNotaFiscal] = useState('')
  const [dataAquisicao, setDataAquisicao] = useState('')
  const [valorAquisicao, setValorAquisicao] = useState<number | null>(null)
  const [proprietario, setProprietario] = useState('')
  const [observacao, setObservacao] = useState('')
  const [atributos, setAtributos] = useState<Record<string, string>>({})
  const [quantidade, setQuantidade] = useState('')
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)
  const [jaCadastrada, setJaCadastrada] = useState<UnidadePorCodigo | null>(null)
  const consultaDaPeca = useRef(0)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  if (!podePatrimonio) {
    return (
      <>
        <PageTitle>Entrada</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>
          Registrar entrada de patrimônio exige a permissão "Entrada de patrimônio".
        </div></Card>
      </>
    )
  }

  const definicoes = detalheQ.data?.atributos ?? []
  const impPeca = impedimentoCadastroPeca(serial, patrimonio)
  // Lista vazia por falha de carregamento é indistinguível de "sem atributos":
  // o estado da consulta entra no gate para que uma não passe pela outra.
  const impAtributos = impedimentoAtributos(definicoes, atributos, { carregando: detalheQ.isLoading, erro: detalheQ.isError })
  const impQtd = impedimentoEntradaQuantidade(quantidade)
  const impOrigem = impedimentoOrigemEntrada(origem, notaFiscal, motivo)
  const faltaBase = !itemId || !localId ? 'Escolha o item e a praça.' : null
  const impedimento = faltaBase ?? impOrigem ?? (ehSerial ? (impPeca ?? impAtributos) : impQtd)
  const exigeNota = origem === 'COMPRA'
  const pendencia = (texto: string | null) => texto && <div style={{ fontSize: 12, color: SEVERIDADE.atencao.texto, marginTop: 4 }}>{texto}</div>
  const rotaDaPeca = jaCadastrada ? `/interno/estoque/equipamentos/${jaCadastrada.id}` : null

  const esquecerPeca = () => { consultaDaPeca.current += 1; setJaCadastrada(null) }

  const limpar = () => {
    setSerial(''); setPatrimonio(''); setSerialFabricante(''); setObservacao('')
    setAtributos({}); setQuantidade(''); esquecerPeca()
  }

  const procurarPeca = async (codigo: string, campo: CampoDuplicado) => {
    const esta = ++consultaDaPeca.current
    let achada: UnidadePorCodigo | null = null
    try {
      const { data } = await getEstoqueUnidadePorCodigo(codigo)
      achada = pecaJaCadastrada(data?.data, campo)
    } catch {
      achada = null
    }
    if (esta === consultaDaPeca.current) setJaCadastrada(achada)
  }

  const registrar = async () => {
    if (!itemId || !localId) return
    setBusy(true)
    try {
      if (ehSerial) {
        const { data } = await createEstoqueUnidade({
          itemId, localId, serial: serial.trim(), patrimonio: patrimonio.trim(),
          serialFabricante: serialFabricante.trim() || null,
          origem, motivo: motivo.trim() || null,
          fornecedor: fornecedor.trim() || null, notaFiscal: notaFiscal.trim() || null,
          dataAquisicao: dataAquisicao || null,
          valorAquisicao,
          proprietario: proprietario.trim() || null, observacao: observacao.trim() || null,
          atributos: Object.keys(atributos).length ? atributos : undefined,
        })
        toast.success('Peça cadastrada e lançada no razão')
        limpar()
        const novaId = data?.data?.id
        if (novaId) navigate(`/interno/estoque/equipamentos/${novaId}`)
      } else {
        await createEstoqueEntradaQuantidade({
          itemId, localId, quantidade: Number(quantidade.trim()),
          origem, notaFiscal: notaFiscal.trim() || null, motivo: motivo.trim() || null,
        })
        toast.success('Entrada registrada')
        limpar()
      }
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      const campo = campoDuplicado(e.codigo)
      if (campo && podeVerPeca) await procurarPeca(campo === 'SERIAL' ? serial.trim() : patrimonio.trim(), campo)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageTitle>Entrada</PageTitle>
      <PageSubtitle>
        A entrada é um lançamento no razão. Compra tem a nota fiscal como lastro; saldo inicial, devolução e outra origem têm o motivo.
      </PageSubtitle>

      {carregandoLocais ? <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
        : erroLocais ? (
          <Card><div style={{ padding: 24, color: BD.red }}>
            Não foi possível carregar as praças. <Button $variant="secondary" onClick={() => recarregarLocais()}>Tentar de novo</Button>
          </div></Card>
        ) : itensQ.isError ? (
          <Card><div style={{ padding: 24, color: BD.red }}>
            Não foi possível carregar o catálogo, e sem ele não há o que dar entrada. <Button $variant="secondary" onClick={() => itensQ.mutate()}>Tentar de novo</Button>
          </div></Card>
        ) : (
          <>
            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field>
                  <label>Item *</label>
                  <Select value={itemId ?? ''} onChange={(e) => { setItemId(Number(e.target.value) || null); setAtributos({}); esquecerPeca() }}>
                    <option value="">— escolha —</option>
                    {itensQ.data.map((i) => <option key={i.id} value={i.id}>{i.nome} ({i.controle === 'SERIAL' ? 'peça' : 'quantidade'})</option>)}
                  </Select>
                </Field>
                <Field>
                  <label>Praça de entrada *</label>
                  <Select value={localId ?? ''} onChange={(e) => setLocalId(Number(e.target.value) || null)}>
                    <option value="">— escolha —</option>
                    {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </Select>
                </Field>
                <Field>
                  <label>Origem da entrada</label>
                  <Select value={origem} onChange={(e) => setOrigem(e.target.value as OrigemEntrada)}>
                    {ORIGENS_ENTRADA.map((o) => <option key={o} value={o}>{ORIGEM_ENTRADA_LABEL[o]}</option>)}
                  </Select>
                  <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>{explicacaoOrigem(origem, ehSerial)}</div>
                </Field>
                <Field>
                  <label>Nota fiscal{exigeNota ? ' *' : ' (opcional)'}</label>
                  <Input value={notaFiscal} maxLength={60} onChange={(e) => setNotaFiscal(e.target.value)} />
                  {exigeNota && pendencia(impOrigem)}
                </Field>
                <Field>
                  <label>Motivo{exigeNota ? ' (opcional)' : ' *'}</label>
                  <Input value={motivo} maxLength={400} onChange={(e) => setMotivo(e.target.value)} />
                  {!exigeNota && pendencia(impOrigem)}
                </Field>
              </div>
            </Card>

            {item && (
              <Card style={{ padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{ehSerial ? 'Dados da peça' : 'Quantidade que entrou'}</div>
                {ehSerial ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                      <Field><label>Serial *</label><CampoComLeitor valor={serial} aoMudar={(v) => { setSerial(v); esquecerPeca() }} tituloLeitor="Ler o serial" rotulo="Serial" /></Field>
                      <Field><label>Patrimônio *</label><CampoComLeitor valor={patrimonio} aoMudar={(v) => { setPatrimonio(v); esquecerPeca() }} tituloLeitor="Ler o patrimônio" rotulo="Patrimônio" /></Field>
                      <Field><label>Série do fabricante (opcional)</label><CampoComLeitor valor={serialFabricante} aoMudar={setSerialFabricante} tituloLeitor="Ler a série do fabricante" rotulo="Série do fabricante" /></Field>
                      <Field><label>Fornecedor</label><Input value={fornecedor} maxLength={160} onChange={(e) => setFornecedor(e.target.value)} /></Field>
                      <Field><label>Data de aquisição</label><Input type="date" max={hoje} value={dataAquisicao} onChange={(e) => setDataAquisicao(e.target.value)} /></Field>
                      <Field><label>Valor de aquisição</label><MoneyInputOpcional value={valorAquisicao} onChange={setValorAquisicao} /></Field>
                      <Field><label>Proprietário</label><Input value={proprietario} maxLength={120} placeholder="BD, quando não informado" onChange={(e) => setProprietario(e.target.value)} /></Field>
                      <Field><label>Observação</label><Input value={observacao} maxLength={500} onChange={(e) => setObservacao(e.target.value)} /></Field>
                    </div>

                    {jaCadastrada && rotaDaPeca && (
                      <div style={{ marginTop: 12, fontSize: 13, color: SEVERIDADE.atencao.texto }}>
                        Peça já cadastrada: {jaCadastrada.itemNome} · {jaCadastrada.localNome} ·{' '}
                        <Ancora href={rotaDaPeca} onClick={(ev) => { ev.preventDefault(); navigate(rotaDaPeca) }}>abrir a ficha da peça</Ancora>
                      </div>
                    )}

                    {detalheQ.isLoading && <div style={{ marginTop: 12 }}><Spinner /></div>}
                    {detalheQ.isError && (
                      <div style={{ marginTop: 12, fontSize: 13, color: BD.red }}>
                        Não foi possível carregar os atributos deste item, e sem eles a peça não pode ser cadastrada.{' '}
                        <Button $variant="secondary" onClick={() => detalheQ.mutate()}>Tentar de novo</Button>
                      </div>
                    )}
                    {definicoes.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Atributos do item</div>
                        <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 8 }}>
                          Definidos no catálogo para este item. O servidor recusa chave que o item não define, obrigatório em branco e valor fora do tipo.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                          {definicoes.map((a) => (
                            <Field key={a.chave}>
                              <label>{a.rotulo}{Number(a.obrigatorio) === 1 ? ' *' : ''}</label>
                              <Input value={atributos[a.chave] ?? ''} maxLength={200}
                                     onChange={(e) => setAtributos((v) => ({ ...v, [a.chave]: e.target.value }))} />
                            </Field>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    <Field><label>Quantidade ({item.unidadeMedida ?? 'un'}) *</label><Input type="number" min={1} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} /></Field>
                  </div>
                )}
              </Card>
            )}

            <SoDesktop>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button disabled={busy || !!impedimento} onClick={registrar}>{ehSerial ? 'Cadastrar peça' : 'Registrar entrada'}</Button>
                {impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{impedimento}</span>}
              </div>
            </SoDesktop>
            <BarraFixa>
              {impedimento && <span style={{ fontSize: 12, color: BD.grayText }}>{impedimento}</span>}
              <Button disabled={busy || !!impedimento} onClick={registrar}>{ehSerial ? 'Cadastrar peça' : 'Registrar entrada'}</Button>
            </BarraFixa>
          </>
        )}
    </>
  )
}

export default EstoqueEntrada
